var _a;
import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { addMinutes, isDate, isSameDay, isSameMonth, isBefore } from "date-fns";
import * as React from "react";
import { forwardRef, useState, useMemo, useCallback } from "react";
import { styled } from "@linaria/react";
import { isDateRange, isDateInterval, isDateAfterType, isDateBeforeType, useDayRender, useActiveModifiers, Button, DayPicker, useDayPicker, useNavigation } from "react-day-picker";
import { IconButton, TextButton } from "@creditornot/cb-button";
import { typographyClassName } from "@creditornot/cb-ingredients";
import { MenuItemGroup, MenuActionItem } from "@creditornot/cb-menu";
import { Popover } from "@creditornot/cb-popover";
import { useDirection, useThemeMode } from "@creditornot/cb-provider";
import { clsx } from "@creditornot/cb-utils";
import { getTimezoneOffset } from "date-fns-tz";
import { Input } from "@creditornot/cb-input";
import "./style.css";
const SvgCalendar = forwardRef((props, ref) => /* @__PURE__ */ jsx("svg", { viewBox: "0 0 24 24", ...props, ref, children: /* @__PURE__ */ jsx("path", { d: "M17.5 0a1 1 0 011 1v1.75c0 .138.112.25.25.25h2.75a2 2 0 012 2v17a2 2 0 01-2 2h-19a2 2 0 01-2-2V5a2 2 0 012-2H4a.5.5 0 01.5.5v2.25a.75.75 0 001.5 0V1a1 1 0 112 0v1.751A.25.25 0 008.25 3h6.25a.5.5 0 01.5.5v2.25a.75.75 0 101.5 0V1a1 1 0 011-1zM21 9H3a.5.5 0 00-.5.5v12a.5.5 0 00.5.5h18a.5.5 0 00.5-.5v-12A.5.5 0 0021 9zM7.5 18.5a1 1 0 010 2h-1a1 1 0 010-2zm5 0a1 1 0 010 2h-1a1 1 0 010-2zm5 0a1 1 0 010 2h-1a1 1 0 010-2zm-10-4a1 1 0 010 2h-1a1 1 0 010-2zm5 0a1 1 0 010 2h-1a1 1 0 010-2zm5 0a1 1 0 010 2h-1a1 1 0 010-2zm-10-4a1 1 0 010 2h-1a1 1 0 010-2zm5 0a1 1 0 010 2h-1a1 1 0 010-2zm5 0a1 1 0 010 2h-1a1 1 0 010-2z" }) }));
const SvgCaretdown = forwardRef((props, ref) => /* @__PURE__ */ jsx("svg", { viewBox: "0 0 24 24", ...props, ref, children: /* @__PURE__ */ jsx("path", { d: "M12.028 19.5c.662 0 1.292-.283 1.73-.778l9.81-11.13a1.84 1.84 0 00-.195-2.568 1.85 1.85 0 00-2.578.134l-8.579 9.732a.25.25 0 01-.376 0L3.262 5.158a1.849 1.849 0 00-3.17.609A1.84 1.84 0 00.49 7.592l9.807 11.126a2.312 2.312 0 001.732.782z" }) }));
const SvgCaretleft = forwardRef((props, ref) => /* @__PURE__ */ jsx("svg", { viewBox: "0 0 24 24", ...props, ref, children: /* @__PURE__ */ jsx("path", { d: "M4.5 11.972c0-.662.283-1.292.778-1.73l11.13-9.81a1.84 1.84 0 012.568.195 1.85 1.85 0 01-.134 2.578L9.11 11.784a.25.25 0 000 .376l9.732 8.578a1.849 1.849 0 01-.609 3.17 1.84 1.84 0 01-1.825-.397L5.282 13.704a2.312 2.312 0 01-.782-1.732z" }) }));
const SvgCaretright = forwardRef((props, ref) => /* @__PURE__ */ jsx("svg", { viewBox: "0 0 24 24", ...props, ref, children: /* @__PURE__ */ jsx("path", { d: "M19.5 11.972c0-.662-.283-1.292-.778-1.73L7.592.433a1.84 1.84 0 00-2.568.195 1.85 1.85 0 00.134 2.578l9.732 8.579a.25.25 0 010 .376l-9.732 8.578a1.849 1.849 0 00.609 3.17 1.84 1.84 0 001.825-.397l11.126-9.807a2.312 2.312 0 00.782-1.732z" }) }));
const defaultLocale = typeof window === "object" && ((_a = window.navigator) == null ? void 0 : _a.language) ? window.navigator.language : "en-GB";
const isDateFnsLocale = (value) => typeof value === "object" && !!value.code;
const getLocaleCode = (locale) => {
  return isDateFnsLocale(locale) ? locale.code : locale || defaultLocale;
};
const getDiff = (date, timezone) => {
  return date.getTimezoneOffset() - getTimezoneOffset(timezone, date) / -6e4;
};
const createDateShiftedToTimezone = (date, timezone) => {
  return addMinutes(date, getDiff(date, timezone));
};
const createDateShiftedFromTimezone = (date, timezone) => {
  return addMinutes(date, getDiff(date, timezone) * -1);
};
function isDateType(value) {
  return isDate(value);
}
const createReactDayPickerValueForTimezone = (value, timezone) => {
  if (isDateType(value))
    return createDateShiftedToTimezone(value, timezone);
  if (Array.isArray(value))
    return value.map(
      (matcher) => createReactDayPickerValueForTimezone(matcher, timezone)
    );
  if (isDateRange(value))
    return {
      from: value.from ? createDateShiftedToTimezone(value.from, timezone) : void 0,
      to: value.to ? createDateShiftedToTimezone(value.to, timezone) : void 0
    };
  if (isDateInterval(value))
    return {
      before: createDateShiftedToTimezone(value.before, timezone),
      after: createDateShiftedToTimezone(value.after, timezone)
    };
  if (isDateAfterType(value))
    return { after: createDateShiftedToTimezone(value.after, timezone) };
  if (isDateBeforeType(value))
    return { before: createDateShiftedToTimezone(value.before, timezone) };
  if (value instanceof Function)
    return (date) => value(createDateShiftedToTimezone(date, timezone));
  return value;
};
const Root$1 = /* @__PURE__ */ styled("div")({
  name: "Root",
  class: "cb_DatePickerBase_Root_rf1j",
  propsAsIs: false
});
const HeaderPlaceHolder = /* @__PURE__ */ styled("div")({
  name: "HeaderPlaceHolder",
  class: "cb_DatePickerBase_HeaderPlaceHolder_h1b0",
  propsAsIs: false
});
const MonthHeader = /* @__PURE__ */ styled("div")({
  name: "MonthHeader",
  class: "cb_DatePickerBase_MonthHeader_m106",
  propsAsIs: false
});
const MonthTitle = /* @__PURE__ */ styled("div")({
  name: "MonthTitle",
  class: "cb_DatePickerBase_MonthTitle_m4jy",
  propsAsIs: false
});
const NavigationContainer = /* @__PURE__ */ styled("div")({
  name: "NavigationContainer",
  class: "cb_DatePickerBase_NavigationContainer_n1qr",
  propsAsIs: false
});
const _exp18 = () => SvgCaretleft;
const CaretLeft = /* @__PURE__ */ styled(_exp18())({
  name: "CaretLeft",
  class: "cb_DatePickerBase_CaretLeft_c9ju",
  propsAsIs: true
});
const _exp19 = () => SvgCaretright;
const CaretRight = /* @__PURE__ */ styled(_exp19())({
  name: "CaretRight",
  class: "cb_DatePickerBase_CaretRight_cxnn",
  propsAsIs: true
});
const Caption = ({
  disableMonthChange,
  disableYearSelect,
  displayMonth,
  locale,
  onYearChange,
  years
}) => {
  const {
    dir,
    numberOfMonths
  } = useDayPicker();
  const {
    displayMonths,
    goToMonth,
    nextMonth,
    previousMonth
  } = useNavigation();
  const displayIndex = displayMonths.findIndex((month) => isSameMonth(displayMonth, month));
  let isFirst = displayIndex === 0;
  let isLast = displayIndex === displayMonths.length - 1;
  if (dir === "rtl") {
    [isLast, isFirst] = [isFirst, isLast];
  }
  const hideNext = disableMonthChange || numberOfMonths > 1 && (isFirst || !isLast);
  const hidePrevious = disableMonthChange || numberOfMonths > 1 && (isLast || !isFirst);
  const handlePreviousClick = () => {
    if (!previousMonth)
      return;
    goToMonth(previousMonth);
  };
  const handleNextClick = () => {
    if (!nextMonth)
      return;
    goToMonth(nextMonth);
  };
  if (!nextMonth && !previousMonth) {
    return null;
  }
  return /* @__PURE__ */ jsxs(NavigationContainer, {
    children: [/* @__PURE__ */ jsx(DatePickerHeader, {
      date: displayMonth,
      disableYearSelect,
      locale,
      onYearChange,
      years
    }), !hidePrevious && /* @__PURE__ */ jsx(IconButton, {
      "aria-label": "Go to previous month",
      background: "transparent",
      disabled: !previousMonth,
      name: "previous-month",
      onClick: handlePreviousClick,
      size: "tiny",
      variant: "neutral",
      children: /* @__PURE__ */ jsx(CaretLeft, {})
    }), !hideNext && /* @__PURE__ */ jsx(IconButton, {
      "aria-label": "Go to next month",
      background: "transparent",
      disabled: !nextMonth,
      name: "next-month",
      onClick: handleNextClick,
      size: "tiny",
      variant: "neutral",
      children: /* @__PURE__ */ jsx(CaretRight, {})
    })]
  });
};
const YearSelectContainer = /* @__PURE__ */ styled("div")({
  name: "YearSelectContainer",
  class: "cb_DatePickerBase_YearSelectContainer_y1kv",
  propsAsIs: false
});
const SelectYearPopoverContent = /* @__PURE__ */ styled("div")({
  name: "SelectYearPopoverContent",
  class: "cb_DatePickerBase_SelectYearPopoverContent_s2re",
  propsAsIs: false
});
const _exp21 = () => ({
  $indicatorColor
}) => $indicatorColor;
const DotIndicator = /* @__PURE__ */ styled("div")({
  name: "DotIndicator",
  class: "cb_DatePickerBase_DotIndicator_d1ir",
  propsAsIs: false,
  vars: {
    "d1ir1otg-0": [_exp21()]
  }
});
const DatePickerHeader = ({
  date,
  disableYearSelect,
  locale,
  onYearChange,
  years
}) => {
  const IntlMonthFormat = useMemo(() => new Intl.DateTimeFormat(locale, {
    month: "long"
  }), [locale]);
  const IntlYearFormat = useMemo(() => new Intl.DateTimeFormat(locale, {
    year: "numeric"
  }), [locale]);
  const yearsToSelect = useMemo(() => {
    if (years) {
      return years;
    }
    const now = date || /* @__PURE__ */ new Date();
    const yearNow = now.getFullYear();
    return [yearNow - 3, yearNow - 2, yearNow - 1, yearNow, yearNow + 1, yearNow + 2];
  }, [date, years]);
  return /* @__PURE__ */ jsxs(MonthHeader, {
    children: [/* @__PURE__ */ jsx(MonthTitle, {
      children: IntlMonthFormat.format(date)
    }), !disableYearSelect && /* @__PURE__ */ jsx(YearSelectContainer, {
      children: /* @__PURE__ */ jsx(Popover, {
        content: /* @__PURE__ */ jsx(SelectYearPopoverContent, {
          children: /* @__PURE__ */ jsx(MenuItemGroup, {
            children: yearsToSelect.map((year) => /* @__PURE__ */ jsx(MenuActionItem, {
              onClick: () => {
                onYearChange(new Date(year, date.getMonth()));
              },
              selected: year === date.getFullYear(),
              variant: "black",
              children: year
            }, year))
          })
        }),
        placement: "bottom-start",
        renderArrow: false,
        rendering: "inline",
        children: ({
          refProps
        }) => /* @__PURE__ */ jsx(TextButton, {
          ...refProps,
          icon: /* @__PURE__ */ jsx(SvgCaretdown, {}),
          iconPosition: "end",
          variant: "grey",
          children: IntlYearFormat.format(date)
        })
      })
    })]
  });
};
const DatePickerBase = ({
  className,
  disabledDays,
  disableMonthChange = false,
  disableOutsideDaysClick = false,
  disableYearSelect = false,
  firstDayOfWeek,
  initialMonth = /* @__PURE__ */ new Date(),
  locale = defaultLocale,
  onDayClick,
  renderDayDotIndicator,
  selectedDays,
  showOutsideDays = true,
  style,
  timezone,
  years,
  ...rest
}) => {
  const direction = useDirection();
  const [selectedMonth, setSelectedMonth] = useState(timezone ? createDateShiftedToTimezone(initialMonth, timezone) : initialMonth);
  const localeCode = getLocaleCode(locale) || defaultLocale;
  const weekStartsOn = useMemo(() => {
    var _a2;
    if (firstDayOfWeek !== void 0)
      return firstDayOfWeek;
    return isDateFnsLocale(locale) ? (_a2 = locale.options) == null ? void 0 : _a2.weekStartsOn : void 0;
  }, [locale, firstDayOfWeek]);
  const handleDayClick = (day, modifiers2) => {
    if (onDayClick && !modifiers2.disabled && !(disableOutsideDaysClick && modifiers2.outside)) {
      onDayClick(timezone ? createDateShiftedFromTimezone(day, timezone) : day);
    }
  };
  const selectedDaysAdjusted = useMemo(() => {
    if (!timezone || !selectedDays)
      return selectedDays;
    return createReactDayPickerValueForTimezone(selectedDays, timezone);
  }, [timezone, selectedDays]);
  const disabledDaysAdjusted = useMemo(() => {
    if (!timezone || !disabledDays)
      return disabledDays;
    return createReactDayPickerValueForTimezone(disabledDays, timezone);
  }, [timezone, disabledDays]);
  const formatters = useMemo(() => {
    const {
      format
    } = new Intl.DateTimeFormat(localeCode, {
      weekday: "narrow"
    });
    return {
      formatWeekdayName: (date) => format(date).toUpperCase()
    };
  }, [localeCode]);
  const labels = useMemo(() => {
    const {
      format
    } = new Intl.DateTimeFormat(localeCode, {
      weekday: "long"
    });
    return {
      labelWeekday: (date) => format(date)
    };
  }, [localeCode]);
  const customComponents = useMemo(() => {
    const {
      format
    } = new Intl.DateTimeFormat(localeCode, {
      day: "numeric"
    });
    return {
      Caption: (props) => /* @__PURE__ */ jsx(Caption, {
        ...props,
        disableMonthChange,
        disableYearSelect,
        locale: localeCode,
        onYearChange: setSelectedMonth,
        years
      }),
      Day: (props) => {
        const buttonRef = React.useRef(null);
        const dayRender = useDayRender(props.date, props.displayMonth, buttonRef);
        const {
          outside
        } = useActiveModifiers(props.date, props.displayMonth);
        if (dayRender.isHidden) {
          return /* @__PURE__ */ jsx("div", {
            role: "gridcell"
          });
        }
        if (!dayRender.isButton || disableOutsideDaysClick && outside) {
          return /* @__PURE__ */ jsx("div", {
            ...dayRender.divProps
          });
        }
        return /* @__PURE__ */ jsx(Button, {
          name: "day",
          ref: buttonRef,
          ...dayRender.buttonProps
        });
      },
      DayContent: ({
        date
      }) => {
        const theme = useThemeMode();
        const indicatorColor = useMemo(() => {
          const indicatorColor2 = renderDayDotIndicator == null ? void 0 : renderDayDotIndicator(date);
          if (!indicatorColor2 || typeof indicatorColor2 === "string")
            return indicatorColor2;
          return indicatorColor2[theme] ?? (theme === "lightHighContrast" ? indicatorColor2.light : null);
        }, [theme, date]);
        return /* @__PURE__ */ jsxs(Fragment, {
          children: [format(date), !!indicatorColor && /* @__PURE__ */ jsx(DotIndicator, {
            $indicatorColor: indicatorColor
          })]
        });
      }
    };
  }, [localeCode, disableOutsideDaysClick, disableMonthChange, disableYearSelect, renderDayDotIndicator, years]);
  const modifiers = useMemo(() => ({
    today: (date) => isSameDay(/* @__PURE__ */ new Date(), timezone ? createDateShiftedFromTimezone(date, timezone) : date)
  }), [timezone]);
  return /* @__PURE__ */ jsxs(Root$1, {
    className: clsx(className, typographyClassName.bodyBase),
    "data-dots": renderDayDotIndicator ? "" : void 0,
    dir: direction,
    style,
    children: [/* @__PURE__ */ jsx(HeaderPlaceHolder, {}), /* @__PURE__ */ jsx(DayPicker, {
      ...rest,
      components: customComponents,
      disabled: disabledDaysAdjusted,
      formatters,
      labels,
      locale: isDateFnsLocale(locale) ? locale : void 0,
      modifiers,
      month: selectedMonth,
      onDayClick: handleDayClick,
      onMonthChange: setSelectedMonth,
      selected: (
        // Typing here seems problematic with `mode` coming from props,
        // see https://github.com/gpbl/react-day-picker/issues/1583
        selectedDaysAdjusted
      ),
      showOutsideDays,
      weekStartsOn
    })]
  });
};
const DatePicker = ({ initialMonth, onChange, timezone, value, ...rest }) => {
  const selectedDays = useMemo(() => value ? [value] : void 0, [value]);
  const handleChange = (day) => {
    if (onChange) {
      onChange(value && isSameDay(day, value) ? void 0 : day);
    }
  };
  return /* @__PURE__ */ jsx(
    DatePickerBase,
    {
      initialMonth: (selectedDays && selectedDays[0]) ?? initialMonth,
      onDayClick: handleChange,
      selectedDays,
      timezone,
      ...rest
    }
  );
};
const DatePicker$1 = DatePicker;
const Root = /* @__PURE__ */ styled("div")({
  name: "Root",
  class: "cb_DatePickerPopover_Root_r1i1",
  propsAsIs: false
});
const DatePickerPopover = ({
  children,
  className,
  content,
  contentZIndex,
  disabled,
  onCloseStart,
  placement = "bottom-start",
  portalElement,
  rendering,
  style
}) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  return /* @__PURE__ */ jsx(Popover, {
    content: typeof content === "function" ? content({
      disabled,
      closePopover: () => setPopoverOpen(false),
      openPopover: () => setPopoverOpen(true),
      isOpen: popoverOpen
    }) : content,
    offset: 4,
    onCloseStart,
    onOpenChange: setPopoverOpen,
    open: disabled ? false : popoverOpen,
    placement,
    portalElement,
    renderArrow: false,
    rendering,
    zIndex: contentZIndex,
    children: ({
      refProps
    }) => /* @__PURE__ */ jsx(Root, {
      role: "group",
      ...refProps,
      className,
      style,
      children: children({
        disabled,
        isOpen: popoverOpen,
        closePopover: () => setPopoverOpen(false),
        openPopover: () => setPopoverOpen(true)
      })
    })
  });
};
const _exp$2 = () => DatePickerPopover;
const _exp2$1 = () => ({
  $stretch
}) => $stretch ? "100%" : "auto";
const StyledDatePickerPopover = /* @__PURE__ */ styled(_exp$2())({
  name: "StyledDatePickerPopover",
  class: "cb_DatePickerInput_StyledDatePickerPopover_sbwm",
  propsAsIs: true,
  vars: {
    "sbwm8ui-0": [_exp2$1()]
  }
});
const DatePickerContainer$2 = /* @__PURE__ */ styled("div")({
  name: "DatePickerContainer",
  class: "cb_DatePickerInput_DatePickerContainer_d7s9",
  propsAsIs: false
});
const DatePickerInput = ({
  children,
  className,
  closeOnChange,
  disableClear,
  disabled,
  disabledDays,
  disableMonthChange,
  disableOutsideDaysClick,
  disableYearSelect,
  formatValue,
  initialMonth,
  inputIcon = /* @__PURE__ */ jsx(SvgCalendar, {}),
  inputId,
  inputName,
  invalid,
  locale = defaultLocale,
  onBlur,
  onChange,
  onFocus,
  placeholder,
  popoverPlacement,
  popoverPortalElement,
  popoverRendering,
  popoverZIndex,
  renderDayDotIndicator,
  rootRef,
  showOutsideDays,
  stretch = false,
  style,
  timezone,
  value,
  years
}, ref) => {
  const IntlDateTimeFormat = useMemo(() => {
    const options = timezone ? {
      timeZone: timezone
    } : void 0;
    return new Intl.DateTimeFormat(getLocaleCode(locale), options);
  }, [locale, timezone]);
  const formatInputValue = formatValue || IntlDateTimeFormat.format;
  const formattedValue = value ? formatInputValue(value, timezone ? {
    timeZone: timezone
  } : void 0) : "";
  return /* @__PURE__ */ jsx(StyledDatePickerPopover, {
    $stretch: stretch,
    content: ({
      closePopover
    }) => /* @__PURE__ */ jsx(DatePickerContainer$2, {
      children: /* @__PURE__ */ jsx(DatePicker$1, {
        disableMonthChange,
        disableOutsideDaysClick,
        disableYearSelect,
        disabledDays,
        initialMonth,
        locale,
        onChange: (day) => {
          if (onChange) {
            onChange(day);
          }
          if (closeOnChange) {
            closePopover();
          }
        },
        renderDayDotIndicator,
        showOutsideDays,
        timezone,
        value,
        years
      })
    }),
    contentZIndex: popoverZIndex,
    disabled,
    onCloseStart: onBlur,
    placement: popoverPlacement,
    portalElement: popoverPortalElement,
    rendering: popoverRendering,
    children: children ? (props) => children({
      ...props,
      value,
      formattedValue
    }) : () => /* @__PURE__ */ jsx(Input, {
      className,
      disabled,
      icon: inputIcon,
      id: inputId,
      invalid,
      name: inputName,
      onClearClick: disableClear ? void 0 : (event) => {
        event.stopPropagation();
        if (onChange) {
          onChange(void 0);
        }
      },
      onFocus,
      placeholder,
      readOnly: true,
      ref,
      rootRef,
      stretch,
      style,
      value: formattedValue
    })
  });
};
const DatePickerInput$1 = forwardRef(DatePickerInput);
const DateMultiPicker = ({ onChange, value = [], ...rest }) => {
  return /* @__PURE__ */ jsx(
    DatePickerBase,
    {
      ...rest,
      initialMonth: value[0],
      onDayClick: (day) => {
        if (onChange) {
          const selectedDay = value.find((d) => isSameDay(d, day));
          onChange(selectedDay ? value.filter((v) => v !== selectedDay) : [...value, day]);
        }
      },
      selectedDays: value
    }
  );
};
const DateMultiPicker$1 = DateMultiPicker;
const EMPTY_SELECTION = [];
const DatePickerContainer$1 = /* @__PURE__ */ styled("div")({
  name: "DatePickerContainer",
  class: "cb_DateMultiPickerInput_DatePickerContainer_drb9",
  propsAsIs: false
});
const DateMultiPickerInput = ({
  children,
  className,
  disableClear,
  disabled,
  disabledDays,
  disableMonthChange,
  disableOutsideDaysClick,
  disableYearSelect,
  formatValue,
  inputIcon = /* @__PURE__ */ jsx(SvgCalendar, {}),
  invalid,
  locale = defaultLocale,
  onBlur,
  onChange,
  onFocus,
  placeholder,
  popoverPlacement,
  popoverZIndex,
  rootRef,
  showOutsideDays,
  style,
  timezone,
  value
}, ref) => {
  const IntlDateTimeFormat = useMemo(() => {
    const options = timezone ? {
      timeZone: timezone
    } : void 0;
    return new Intl.DateTimeFormat(getLocaleCode(locale), options);
  }, [locale, timezone]);
  const defaultFormatValue = useCallback((value2) => {
    if (value2.length === 0) {
      return "";
    }
    if (value2.length === 1) {
      return IntlDateTimeFormat.format(value2[0]);
    }
    return `${value2.length} dates selected`;
  }, [IntlDateTimeFormat]);
  const formattedValue = value && value.length > 0 ? (formatValue || defaultFormatValue)(value, {
    timeZone: timezone
  }) : "";
  return /* @__PURE__ */ jsx(DatePickerPopover, {
    content: /* @__PURE__ */ jsx(DatePickerContainer$1, {
      children: /* @__PURE__ */ jsx(DateMultiPicker$1, {
        disableMonthChange,
        disableOutsideDaysClick,
        disableYearSelect,
        disabledDays,
        locale,
        onChange,
        showOutsideDays,
        timezone,
        value
      })
    }),
    contentZIndex: popoverZIndex,
    onCloseStart: onBlur,
    placement: popoverPlacement,
    children: children ? (props) => children({
      ...props,
      value,
      formattedValue
    }) : () => /* @__PURE__ */ jsx(Input, {
      className,
      disabled,
      icon: inputIcon,
      invalid,
      onClearClick: disableClear ? void 0 : (event) => {
        event.stopPropagation();
        if (onChange) {
          onChange(EMPTY_SELECTION);
        }
      },
      onFocus,
      placeholder,
      readOnly: true,
      ref,
      rootRef,
      style,
      value: formattedValue
    })
  });
};
const DateMultiPickerInput$1 = forwardRef(DateMultiPickerInput);
const _exp$1 = () => DatePickerBase;
const StyledDatePickerBase = /* @__PURE__ */ styled(_exp$1())({
  name: "StyledDatePickerBase",
  class: "cb_DateRangePicker_StyledDatePickerBase_s1sh",
  propsAsIs: true
});
const DateRangePicker = ({
  onChange,
  onDayClick,
  value = {},
  ...rest
}) => {
  const [lastSelection, setLastSelection] = useState();
  const {
    from,
    to
  } = value;
  return /* @__PURE__ */ jsx(StyledDatePickerBase, {
    initialMonth: value.from || value.to,
    ...rest,
    mode: "range",
    onDayClick: (day) => {
      if (onDayClick) {
        onDayClick(day);
      }
      if (!onChange) {
        return;
      }
      if (!from || lastSelection === void 0 || lastSelection === "to") {
        setLastSelection("from");
        return onChange({
          from: day,
          to: to && to >= day ? to : void 0
        });
      }
      if (from && day < from) {
        setLastSelection("to");
        return onChange({
          from: day,
          to: from
        });
      }
      if (from && day >= from) {
        setLastSelection("to");
        return onChange({
          from,
          to: day
        });
      }
    },
    selectedDays: value
  });
};
const DateRangePicker$1 = DateRangePicker;
const DatePickerContainer = /* @__PURE__ */ styled("div")({
  name: "DatePickerContainer",
  class: "cb_DateRangePickerInput_DatePickerContainer_d1q4",
  propsAsIs: false
});
const _exp = () => Input;
const _exp2 = () => ({
  onClearClick
}) => onClearClick ? 240 : 220;
const StyledInput = /* @__PURE__ */ styled(_exp())({
  name: "StyledInput",
  class: "cb_DateRangePickerInput_StyledInput_s156",
  propsAsIs: true,
  vars: {
    "s1565m1r-0": [_exp2(), "px"]
  }
});
const DateRangePickerInput = ({
  children,
  className,
  disableClear,
  disabled,
  formatValue,
  inputIcon = /* @__PURE__ */ jsx(SvgCalendar, {}),
  inputId,
  inputName,
  invalid,
  locale = defaultLocale,
  onBlur,
  onChange,
  onFocus,
  placeholder,
  popoverPlacement,
  popoverPortalElement,
  popoverRendering,
  popoverZIndex,
  rootRef,
  style,
  timezone,
  value,
  ...datePickerProps
}, ref) => {
  const IntlDateTimeFormat = useMemo(() => {
    const options = timezone ? {
      timeZone: timezone
    } : void 0;
    return new Intl.DateTimeFormat(getLocaleCode(locale), options);
  }, [locale, timezone]);
  const [firstSelection, setFirstSelection] = useState(void 0);
  const valueToShow = useMemo(() => firstSelection ? {
    from: firstSelection
  } : value, [firstSelection, value]);
  const defaultFormatValue = useCallback((value2) => {
    if (!value2.from && !value2.to) {
      return "";
    }
    return `${value2.from ? IntlDateTimeFormat.format(value2.from) : ""} – ${value2.to ? IntlDateTimeFormat.format(value2.to) : ""}`;
  }, [IntlDateTimeFormat]);
  const formattedValue = value ? (formatValue || defaultFormatValue)(value, {
    timeZone: timezone
  }) : "";
  return /* @__PURE__ */ jsx(DatePickerPopover, {
    content: ({
      closePopover
    }) => /* @__PURE__ */ jsx(DatePickerContainer, {
      children: /* @__PURE__ */ jsx(DateRangePicker$1, {
        ...datePickerProps,
        onDayClick: (day) => {
          if (firstSelection === void 0) {
            setFirstSelection(day);
          } else {
            onChange(isBefore(firstSelection, day) ? {
              from: firstSelection,
              to: day
            } : {
              from: day,
              to: firstSelection
            });
            setFirstSelection(void 0);
            closePopover();
          }
        },
        value: valueToShow
      })
    }),
    contentZIndex: popoverZIndex,
    disabled,
    onCloseStart: onBlur,
    placement: popoverPlacement,
    portalElement: popoverPortalElement,
    rendering: popoverRendering,
    children: children ? (props) => children({
      ...props,
      value,
      formattedValue
    }) : () => /* @__PURE__ */ jsx(StyledInput, {
      className,
      disabled,
      icon: inputIcon,
      id: inputId,
      invalid,
      name: inputName,
      onClearClick: disableClear ? void 0 : () => {
        if (onChange) {
          onChange(void 0);
        }
      },
      onFocus: (event) => {
        setFirstSelection(void 0);
        if (onFocus) {
          onFocus(event);
        }
      },
      placeholder,
      readOnly: true,
      ref,
      rootRef,
      style,
      value: formattedValue
    })
  });
};
const DateRangePickerInput$1 = forwardRef(DateRangePickerInput);
export {
  DateMultiPicker$1 as DateMultiPicker,
  DateMultiPickerInput$1 as DateMultiPickerInput,
  DatePicker$1 as DatePicker,
  DatePickerInput$1 as DatePickerInput,
  DateRangePicker$1 as DateRangePicker,
  DateRangePickerInput$1 as DateRangePickerInput
};
//# sourceMappingURL=index.js.map
